import React, { FunctionComponent, useCallback, useState } from "react";
import { useIntl } from "react-intl";
import {
  MdCheckCircle,
  MdRemove,
} from "react-icons/md";
import {
  Icon,
  Spinner,
  Table,
  TableProps,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Heading,
  Badge,
  Stack,
} from "@chakra-ui/react";

import { SiteAlarmType_siteAlarmType_events } from "graphql/generated";

import { Timestamp, RelativeTimestamp } from "common/hooks/time";

import NotificationIcon from "../notifications/NotificationIcon";


interface IProps extends TableProps {
  count: number;
  items: SiteAlarmType_siteAlarmType_events[] | undefined;
  onlyActive: boolean;
  timezone: string;
}


const AlarmEvents: FunctionComponent<IProps> = ({
  count,
  items,
  onlyActive,
  timezone,
  ...rest
}) => {
  const intl = useIntl();
  const textColor = useColorModeValue("black", "white");
  const bgColor = useColorModeValue("white", "black");
  const [relativeTimestamp, setRelativeTimestamp] = useState<boolean>(false);
  const toggleTimestampFormat = useCallback(() => setRelativeTimestamp(!relativeTimestamp), [relativeTimestamp]);

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Heading>
          {intl.formatMessage({
            id: "alarms__alarm_type__events",
            defaultMessage: "Events",
          })}
          <Badge fontSize="sm" bg={textColor} color={bgColor} borderRadius={15} px={2} py={1} ml={2} title={intl.formatMessage({
            id: "alarms__alarm_type__events_number_of",
            defaultMessage: "Number of events in the incident",
          })}>
            {count}
          </Badge>
        </Heading>
      </Stack>
      {
        items ? (
          !!items.length
          && <Table variant="striped" size="sm" {...rest}>
            <Thead>

              <Tr>
                <Th>
                  {intl.formatMessage({
                    id: "alarms__events__column__level",
                    defaultMessage: "Level",
                  })}
                </Th>
                <Th width="90%">
                  {intl.formatMessage({
                    id: "alarms__events__column__text",
                    defaultMessage: "Message",
                  })}
                </Th>
                <Th>
                  {intl.formatMessage({
                    id: "alarms__events__column__active",
                    defaultMessage: "Active",
                  })}
                </Th>
                <Th>
                  {intl.formatMessage({
                    id: "alarms__events__column__timestamp_creation",
                    defaultMessage: "Time",
                  })}
                </Th>
              </Tr>

            </Thead>
            <Tbody>

              {
                items.map((alarm) => {
                  return (
                    <Tr key={alarm.id}>
                      <Td whiteSpace="nowrap" title={
                        intl.formatMessage({
                          id: "alarms__events__column__level",
                          defaultMessage: "Level",
                        })
                      }>
                        <NotificationIcon alarmType={alarm.alarmType} onlyActive={onlyActive} />
                      </Td>
                      <Td title={
                        intl.formatMessage({
                          id: "alarms__events__column__text",
                          defaultMessage: "Message",
                        })
                      }>
                        {alarm.text}
                      </Td>
                      <Td title={
                        alarm.active
                        ? intl.formatMessage({
                          id: "alarms__events__column__active",
                          defaultMessage: "Active",
                        })
                        : intl.formatMessage({
                          id: "alarms__events__column__inactive",
                          defaultMessage: "Inactive",
                        })
                      }>
                        <Icon as={alarm.active ? MdCheckCircle : MdRemove} />
                      </Td>
                      <Td whiteSpace="nowrap" onClick={toggleTimestampFormat} title={
                        intl.formatMessage({
                          id: "alarms__events__column__timestamp_creation",
                          defaultMessage: "Time",
                        })
                      }>
                        {
                          relativeTimestamp
                          ? <RelativeTimestamp date={`${alarm.createTimestamp}+00:00`} />
                          : <Timestamp date={`${alarm.createTimestamp}+00:00`} />
                        }
                      </Td>
                    </Tr>
                  );
                })
              }

            </Tbody>
          </Table>
        )
        :
        <Spinner size="lg" m="4" />
      }
    </>
  );
};

export default AlarmEvents;
